import { UploadDocumentJob } from '@domain/types';
import { AnalyticsBrowser, Options } from '@segment/analytics-next';

import type { TerminalImportStatus } from '../../common/document-import/uploadStatus';
import * as Segments from './typewriter/segment';

export const UPLOAD_SOURCE_MAP: Record<
  UploadDocumentJob['uploadSource'],
  Segments.BatchFileUploadStartSource
> = {
  watcher: Segments.BatchFileUploadStartSource.Outbox,
  button: Segments.BatchFileUploadStartSource.Button,
  drop: Segments.BatchFileUploadStartSource.DragDrop,
  opening: Segments.BatchFileUploadStartSource.Opening,
};

export function getUploadDurationMs(startDate: Date | undefined): number | undefined {
  if (!startDate) return undefined;
  return Math.max(0, Math.round(Date.now() - startDate.getTime()));
}

type UploadTrackingDelegateOptions = {
  analytics: AnalyticsBrowser;
  getOptions: () => Options;
};

export interface BatchFileUploadStartParams {
  documentsCount: number;
  uploadSource: UploadDocumentJob['uploadSource'];
  batchUploadId: string;
}

export interface BatchFileUploadEndParams {
  documentsCount: number;
  batchUploadId?: string;
  startDate?: Date;
}

export interface FileUploadStartedParams {
  uploadJobId: string;
  uploadSource: UploadDocumentJob['uploadSource'];
  flowType?: UploadDocumentJob['flowType'];
  batchUploadId?: string;
}

export interface FileUploadedParams {
  documentId: string;
  uploadSource: UploadDocumentJob['uploadSource'];
  uploadJobId: string;
  startDate: Date;
  flowType?: UploadDocumentJob['flowType'];
  batchUploadId?: string;
}

export interface FileUploadFailedParams {
  errorCause?: string;
  uploadJobId: string;
  startDate: Date;
  flowType?: UploadDocumentJob['flowType'];
  batchUploadId?: string;
}

export type ImportCompletionSource = 'realtime' | 'poller_give_up';

export type DocumentOrigin = 'single' | 'split' | 'publipostage';

export interface FileImportCompletedParams {
  documentId: string;
  uploadJobId: string;
  uploadSource: UploadDocumentJob['uploadSource'];
  startDate: Date;
  flowType?: UploadDocumentJob['flowType'];
  batchUploadId?: string;
  terminalStatus: TerminalImportStatus;
  completionSource: ImportCompletionSource;
  documentOrigin: DocumentOrigin;
  parentDocumentId?: string;
}

export function createUploadTrackingDelegate({
  analytics,
  getOptions,
}: UploadTrackingDelegateOptions) {
  return {
    trackBatchFileUploadStart({
      documentsCount,
      uploadSource,
      batchUploadId,
    }: BatchFileUploadStartParams): void {
      analytics.track(
        'Batch File Upload Start',
        {
          documentsCount,
          source: UPLOAD_SOURCE_MAP[uploadSource],
          batchUploadId,
        },
        getOptions(),
      );
    },

    trackBatchFileUploadEnd({
      documentsCount,
      batchUploadId,
      startDate,
    }: BatchFileUploadEndParams): void {
      analytics.track(
        'Batch File Upload End',
        { documentsCount, batchUploadId, durationMs: getUploadDurationMs(startDate) },
        getOptions(),
      );
    },

    trackFileUploadStarted({
      uploadJobId,
      uploadSource,
      flowType,
      batchUploadId,
    }: FileUploadStartedParams): void {
      analytics.track(
        'File Upload Started',
        {
          uploadJobId,
          source: UPLOAD_SOURCE_MAP[uploadSource],
          flowType,
          batchUploadId,
        },
        getOptions(),
      );
    },

    trackFileUploaded({
      documentId,
      uploadSource,
      uploadJobId,
      startDate,
      flowType,
      batchUploadId,
    }: FileUploadedParams): void {
      analytics.track(
        'File Uploaded',
        {
          fhirReference: documentId,
          source: UPLOAD_SOURCE_MAP[uploadSource],
          uploadJobId,
          durationMs: getUploadDurationMs(startDate),
          batchUploadId,
          flowType,
        },
        getOptions(),
      );
    },

    trackFileUploadFailed({
      errorCause,
      uploadJobId,
      startDate,
      flowType,
      batchUploadId,
    }: FileUploadFailedParams): void {
      const props: Segments.FileUploadFailed = {
        errorCause,
        uploadJobId,
        durationMs: getUploadDurationMs(startDate),
        batchUploadId,
        flowType,
      };

      analytics.track('File Upload Failed', props, getOptions());
    },

    trackFileImportCompleted({
      documentId,
      uploadJobId,
      uploadSource,
      startDate,
      flowType,
      batchUploadId,
      terminalStatus,
      completionSource,
      documentOrigin,
      parentDocumentId,
    }: FileImportCompletedParams): void {
      analytics.track(
        'File Import Completed',
        {
          fhirReference: documentId,
          uploadJobId,
          source: UPLOAD_SOURCE_MAP[uploadSource],
          durationMs: getUploadDurationMs(startDate),
          flowType,
          batchUploadId,
          terminalStatus,
          completionSource,
          documentOrigin,
          parentDocumentId,
        },
        getOptions(),
      );
    },
  };
}
